
import React, { useCallback, useMemo } from "react"
import Image from "next/image"

import { useFieldsetModel } from "@lib"
import { OMReference } from "@apptivity-lab/firmament-node-sdk"

import { CnxMeeting, } from "@firmament-packages/connectx"
import AffRating from "../AffRating"
import { AffExpertProfileMiniView } from "@components/domain/AffExpertProfile"

import AffExpertProfile from "../AffExpertProfile"
import AsyncButton from "@components/AsyncButton"

const AffRatingForm: React.FunctionComponent<{
    value?: AffRating,
    subject?: CnxMeeting,
    error?: Error,
    submitting?: boolean,
    onSubmit?: (affRating: AffRating) => void
}> = ({ value, subject, error, submitting, onSubmit }) => {
    const newAffRating = useMemo(() => {
        const rating = AffRating.createPlaceholder(
            subject ? new OMReference(CnxMeeting, subject.id) : undefined,
        )
        return rating
    }, [subject])
    const [model, setModel] = useFieldsetModel(newAffRating, value, undefined)

    const expertProfile = useMemo(() => {
        if (subject?.provider) {
            return new OMReference(AffExpertProfile, subject.provider.id).actualObject
        } else {
            return null
        }
    }, [subject])

    const onFormSubmit = useCallback((event) => {
        event.preventDefault()

        if (!model) {
            return
        }

        onSubmit && onSubmit(new AffRating(model))
    }, [model, onSubmit])

    return (
        <form id="rating-form" className="rating-form" onSubmit={onFormSubmit}>
            <div className="flex flex-col">
                <h3 className="text-headline text-black-900">
                    {subject?.title}
                </h3>

                {expertProfile && <AffExpertProfileMiniView className="mt-[8px]" expertProfile={expertProfile} />}

                <div className="mt-[16px] flex gap-x-[16px]">
                    {[1, 2, 3, 4, 5].map((star) => {
                        const overall = model?.overall || 0
                        const filled = overall >= star

                        return (
                            <button type="button" key={star}
                                onClick={() => setModel(Object.assign({}, model, { overall: star }))}>
                                <Image width="44" height="44"
                                    src={filled ? "/images/ic_star_rating_full_big.svg" : "/images/ic_star_rating_empty_big.svg"}
                                    alt={`${star} ${star === 1 ? "Star" : "Stars"}`} />
                            </button>
                        )
                    })}
                </div>

                <hr className="bg-black-100 my-[16px]" />

                <p className="text-footnote text-black-700">Please tell us more about your experience in this class - your feedback is invaluable to other members!</p>
                <textarea
                    className="mt-[8px] px-[16px] py-[12px] border-[1px] border-black-500 rounded-[8px] text-black-900 text-body"
                    rows={3}
                    value={model?.review || ""}
                    onChange={(event) => setModel(Object.assign({}, model, { review: event.target.value }))}
                />

                {error && <p className="p-[8px] mt-[32px] text-alert-900 bg-alert-100">{error.message}</p>}

                <AsyncButton type="submit" className="w-full mt-[32px] button-primary" loading={submitting}>
                    Submit Review
                </AsyncButton>
            </div>
        </form>
    )
}

export default AffRatingForm
